import axios, { AxiosInstance } from 'axios';
import type {
  LojistaAdmin,
  CriarLojistaPayload,
  EditarLojistaPayload,
  ResetSenhaPayload,
  VinculoLojista,
  EstabelecimentoAdmin,
} from '../tipos';

/**
 * Serviço administrativo para gerenciamento de lojistas, vínculos e estabelecimentos.
 * Requisitos: 2.1, 3.1, 4.1, 5.1, 6.1, 7.1, 8.1, 9.1
 *
 * - Todas as requisições usam token JWT no header Authorization
 * - Base URL: VITE_API_CENTRAL_URL + /api/central/admin
 * - Timeout de 10 segundos
 */

const TIMEOUT_MS = 10_000;

class ServicoAdmin {
  private readonly cliente: AxiosInstance;

  constructor() {
    const urlBase = import.meta.env.VITE_API_CENTRAL_URL || '';

    this.cliente = axios.create({
      baseURL: `${urlBase}/api/central/admin`,
      timeout: TIMEOUT_MS,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor de requisição: adiciona token JWT ao header Authorization
    this.cliente.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('nexo_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor de resposta: trata erros comuns (401, 403)
    this.cliente.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('nexo_token');
          localStorage.removeItem('nexo_token_expiracao');
          window.dispatchEvent(new CustomEvent('nexo:sessao-expirada'));
        }
        return Promise.reject(error);
      }
    );
  }

  // ============================================================
  // Lojistas
  // ============================================================

  /**
   * Lista todos os lojistas cadastrados.
   * GET /api/central/admin/lojistas
   */
  async listarLojistas(): Promise<LojistaAdmin[]> {
    const resposta = await this.cliente.get<Record<string, unknown>>('/lojistas');
    const dados = resposta.data;

    // A API pode retornar { lojistas: [...] } ou diretamente um array
    let lista: Array<Record<string, unknown>>;
    if (Array.isArray(dados)) {
      lista = dados;
    } else if (dados && Array.isArray((dados as Record<string, unknown>).lojistas)) {
      lista = (dados as Record<string, unknown>).lojistas as Array<Record<string, unknown>>;
    } else {
      lista = [];
    }

    return lista.map((row) => ({
      id: row.id as string,
      nome: row.nome as string,
      email: row.email as string,
      ativo: row.ativo as boolean,
      ehAdmin: (row.eh_admin ?? row.ehAdmin ?? false) as boolean,
      criadoEm: (row.criado_em ?? row.criadoEm ?? '') as string,
      atualizadoEm: (row.atualizado_em ?? row.atualizadoEm ?? '') as string,
    }));
  }

  /**
   * Cria um novo lojista.
   * POST /api/central/admin/lojistas
   */
  async criarLojista(payload: CriarLojistaPayload): Promise<LojistaAdmin> {
    const resposta = await this.cliente.post<Record<string, unknown>>('/lojistas', {
      nome: payload.nome,
      email: payload.email,
      senha: payload.senha,
      eh_admin: payload.ehAdmin ?? false,
    });
    const row = resposta.data;
    return {
      id: row.id as string,
      nome: row.nome as string,
      email: row.email as string,
      ativo: row.ativo as boolean,
      ehAdmin: (row.eh_admin ?? row.ehAdmin ?? false) as boolean,
      criadoEm: (row.criado_em ?? row.criadoEm ?? '') as string,
      atualizadoEm: (row.atualizado_em ?? row.atualizadoEm ?? '') as string,
    };
  }

  /**
   * Edita os dados de um lojista existente.
   * PUT /api/central/admin/lojistas/:id
   */
  async editarLojista(id: string, payload: EditarLojistaPayload): Promise<LojistaAdmin> {
    const resposta = await this.cliente.put<Record<string, unknown>>(`/lojistas/${id}`, {
      nome: payload.nome,
      email: payload.email,
      eh_admin: payload.ehAdmin,
    });
    const row = resposta.data;
    return {
      id: row.id as string,
      nome: row.nome as string,
      email: row.email as string,
      ativo: row.ativo as boolean,
      ehAdmin: (row.eh_admin ?? row.ehAdmin ?? false) as boolean,
      criadoEm: (row.criado_em ?? row.criadoEm ?? '') as string,
      atualizadoEm: (row.atualizado_em ?? row.atualizadoEm ?? '') as string,
    };
  }

  /**
   * Ativa um lojista.
   * PATCH /api/central/admin/lojistas/:id/ativar
   */
  async ativarLojista(id: string): Promise<void> {
    await this.cliente.patch(`/lojistas/${id}/ativar`);
  }

  /**
   * Desativa um lojista.
   * PATCH /api/central/admin/lojistas/:id/desativar
   */
  async desativarLojista(id: string): Promise<void> {
    await this.cliente.patch(`/lojistas/${id}/desativar`);
  }

  /**
   * Reseta a senha de um lojista.
   * PATCH /api/central/admin/lojistas/:id/reset-senha
   */
  async resetarSenha(id: string, payload: ResetSenhaPayload): Promise<void> {
    await this.cliente.patch(`/lojistas/${id}/reset-senha`, { nova_senha: payload.novaSenha });
  }

  // ============================================================
  // Vínculos
  // ============================================================

  /**
   * Lista os vínculos (estabelecimentos) de um lojista.
   * GET /api/central/admin/lojistas/:id/vinculos
   */
  async listarVinculos(idLojista: string): Promise<VinculoLojista[]> {
    const resposta = await this.cliente.get<VinculoLojista[]>(
      `/lojistas/${idLojista}/vinculos`
    );
    return resposta.data;
  }

  /**
   * Cria um vínculo entre lojista e estabelecimento.
   * POST /api/central/admin/vinculos
   */
  async criarVinculo(idLojista: string, idEstabelecimento: string): Promise<void> {
    await this.cliente.post('/vinculos', { id_lojista: idLojista, id_estabelecimento: idEstabelecimento });
  }

  /**
   * Remove um vínculo entre lojista e estabelecimento.
   * DELETE /api/central/admin/vinculos/:idLojista/:idEstabelecimento
   */
  async removerVinculo(idLojista: string, idEstabelecimento: string): Promise<void> {
    await this.cliente.delete(`/vinculos/${idLojista}/${idEstabelecimento}`);
  }

  // ============================================================
  // Estabelecimentos
  // ============================================================

  /**
   * Lista todos os estabelecimentos disponíveis.
   * GET /api/central/admin/estabelecimentos
   */
  async listarEstabelecimentos(): Promise<EstabelecimentoAdmin[]> {
    const resposta = await this.cliente.get<Record<string, unknown>>('/estabelecimentos');
    const dados = resposta.data;

    // A API pode retornar { estabelecimentos: [...] } ou diretamente um array
    let lista: Array<Record<string, unknown>>;
    if (Array.isArray(dados)) {
      lista = dados;
    } else if (dados && Array.isArray((dados as Record<string, unknown>).estabelecimentos)) {
      lista = (dados as Record<string, unknown>).estabelecimentos as Array<Record<string, unknown>>;
    } else {
      lista = [];
    }

    return lista.map((row) => ({
      id: row.id as string,
      nome: row.nome as string,
      pontoAcessoApi: (row.ponto_acesso_api ?? row.pontoAcessoApi ?? '') as string,
      ativo: (row.ativo ?? true) as boolean,
      criadoEm: (row.criado_em ?? row.criadoEm ?? '') as string,
    }));
  }
}

export const servicoAdmin = new ServicoAdmin();
